import type { InstructorPayoutBasis } from "@/modules/master/domain/contracts/instructors";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import {
  Button,
  Dialog,
  DialogContent,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@devhop/ui";
import { useState } from "react";
import {
  useGuestInstructorCheckInClassSessionMutation,
  useGuestInstructorCheckInPrivateBookingMutation,
} from "../api/queries";

type AddGuestInstructorDialogProps = {
  sessionId: string;
  sessionType: "general_class" | "private_lesson";
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  assignedInstructorIds?: string[];
  assignedInstructorNames?: string[];
};

export function AddGuestInstructorDialog({
  sessionId,
  sessionType,
  open,
  onOpenChange,
  onSuccess,
  assignedInstructorIds = [],
  assignedInstructorNames = [],
}: AddGuestInstructorDialogProps) {
  const [name, setName] = useState<string>("");
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [ratePerSession, setRatePerSession] = useState<string>("");
  const [payoutBasis, setPayoutBasis] =
    useState<InstructorPayoutBasis>("per_hour");
  const [substituteForInstructorId, setSubstituteForInstructorId] =
    useState<string>("");
  const [substituteReason, setSubstituteReason] = useState<string>("");

  const guestClassMutation = useGuestInstructorCheckInClassSessionMutation();
  const guestPrivateMutation =
    useGuestInstructorCheckInPrivateBookingMutation();

  const isSubmitting =
    guestClassMutation.isPending || guestPrivateMutation.isPending;

  const handleSubmit = async () => {
    if (!name.trim()) return;

    try {
      const payload = {
        name: name.trim(),
        phoneNumber: phoneNumber.trim() || null,
        email: email.trim() || null,
        substituteForInstructorId: substituteForInstructorId || null,
        substituteReason: substituteReason || null,
        ratePerSession: ratePerSession
          ? Number.parseInt(ratePerSession, 10)
          : null,
        payoutBasis,
      };

      if (sessionType === "general_class") {
        await guestClassMutation.mutateAsync({
          sessionId,
          ...payload,
        });
      } else {
        await guestPrivateMutation.mutateAsync({
          bookingId: sessionId,
          ...payload,
        });
      }
      handleClose();
      onSuccess();
    } catch {
      // Error handling is done in mutation
    }
  };

  const handleClose = () => {
    setName("");
    setPhoneNumber("");
    setEmail("");
    setRatePerSession("");
    setPayoutBasis("per_hour");
    setSubstituteForInstructorId("");
    setSubstituteReason("");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay />
        <DialogContent className="max-w-md gap-4 sm:max-w-md">
          <DialogTitle className="font-semibold text-lg">
            ເພີ່ມຄູສອນຊົ່ວຄາວ
          </DialogTitle>

          <div className="space-y-4">
            <div>
              <Label
                htmlFor="name"
                className="mb-1.5 block font-medium text-sm"
              >
                ຊື່ຄູສອນ *
              </Label>
              <Input
                id="name"
                placeholder="ປ້ອນຊື່ຄູສອນ..."
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full"
              />
            </div>

            <div>
              <Label
                htmlFor="phone"
                className="mb-1.5 block font-medium text-sm"
              >
                ເບີໂທລະສັບ
              </Label>
              <Input
                id="phone"
                placeholder="ປ້ອນເບີໂທລະສັບ..."
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                className="w-full"
                type="tel"
              />
            </div>

            <div>
              <Label
                htmlFor="email"
                className="mb-1.5 block font-medium text-sm"
              >
                ອີເມວ
              </Label>
              <Input
                id="email"
                placeholder="ປ້ອນອີເມວ..."
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full"
                type="email"
              />
            </div>

            <div>
              <Label
                htmlFor="guest-payout-basis"
                className="mb-1.5 block font-medium text-sm"
              >
                ວິທີຄິດຄ່າຈ້າງ (ໃຊ້ເຫມືອນກັນທຸກປະເພດວຽກ)
              </Label>
              <Select
                value={payoutBasis}
                onValueChange={(v) =>
                  setPayoutBasis(v as InstructorPayoutBasis)
                }
              >
                <SelectTrigger id="guest-payout-basis" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="per_hour">ຕໍ່ຊົ່ວໂມງ</SelectItem>
                  <SelectItem value="per_session">
                    ຕໍ່ຄັ້ງ (ຊົ່ວໂມງຮຽນ / ການຈອງ)
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label
                htmlFor="rate"
                className="mb-1.5 block font-medium text-sm"
              >
                ຈຳນວນເງິນ (ກີບ) — ຕາມຖານທີ່ເລືອກ
              </Label>
              <Input
                id="rate"
                placeholder="0"
                value={ratePerSession}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === "" || /^\d+$/.test(val)) {
                    setRatePerSession(val);
                  }
                }}
                className="w-full"
                type="text"
                inputMode="numeric"
              />
            </div>

            <div>
              <Label
                htmlFor="substitute-for"
                className="mb-1.5 block font-medium text-sm"
              >
                ແທນຄູສອນ (ບໍ່ບັງຄັບ)
              </Label>
              {assignedInstructorIds.length > 0 ? (
                <SimpleSelect
                  value={substituteForInstructorId || undefined}
                  onValueChange={(val) =>
                    setSubstituteForInstructorId(val || "")
                  }
                  placeholder="ເລືອກຄູສອນທີ່ຖືກແທນ..."
                  className="w-full"
                  clearable
                  options={assignedInstructorIds.map((id, index) => ({
                    value: id,
                    label: assignedInstructorNames[index] || id,
                  }))}
                />
              ) : (
                <Input
                  id="substitute-for"
                  placeholder="ບໍ່ມີຄູສອນທີ່ຖືກກຳນົດໄວ້"
                  disabled
                  className="w-full"
                />
              )}
            </div>

            <div>
              <Label
                htmlFor="reason"
                className="mb-1.5 block font-medium text-sm"
              >
                ເຫດຜົນ (ບໍ່ບັງຄັບ)
              </Label>
              <textarea
                id="reason"
                placeholder="ເຊັ່ນ ຄູເກົ່າປ່ວຍ, ຄູເກົ່າຕິດທຸລະກິດ..."
                value={substituteReason}
                onChange={(e) => setSubstituteReason(e.target.value)}
                className="flex min-h-[80px] w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              ຍົກເລີກ
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!name.trim() || isSubmitting}
            >
              {isSubmitting ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກ"}
            </Button>
          </div>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
}
